import React, { useState } from "react";
import { Pressable, Text, View } from "react-native";

import AIExhibitNarrationPressable from "../audio/AIExhibitNarrationPressable";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";

const NARRATION_BY_SCENARIO_ID = Object.freeze({
  unverified_citation: "ai_mistake_unverified_citation",
  distribution_shift_snow: "ai_mistake_distribution_shift",
  historical_bias_hiring: "ai_mistake_historical_bias",
  wrong_objective_loophole: "ai_mistake_wrong_objective",
  ambiguous_bank: "ai_mistake_ambiguous_context",
});
import { getAIExhibitBatch3Copy } from "../content/aiExhibitBatch3Content";

export default function AIMistakesScreen({
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const t = getAIExhibitBatch3Copy(language).mistakes;
  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const scenario = t.scenarios[index];
  const correct = choice === scenario.answer;

  const next = () => {
    setIndex((current) => (current + 1) % t.scenarios.length);
    setChoice(null);
    setRevealed(false);
  };

  return (
    <View style={{ padding: 28 }}>
      <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900", letterSpacing: 1.2 }}>{t.eyebrow}</Text>
      <Text style={{ color: C.text, fontSize: 44, lineHeight: 50, fontWeight: "900", marginTop: 8 }}>{t.title}</Text>
      <Text style={{ color: C.muted, fontSize: 18, lineHeight: 27, marginTop: 8, maxWidth: 970 }}>{t.subtitle}</Text>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 18, marginTop: 24 }}>
        <View style={{ flex: 1, minWidth: 390, padding: 22, borderRadius: 19, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}>
          <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900" }}>
            {t.scenario.toUpperCase()} {index + 1} / {t.scenarios.length}
          </Text>
          <Text style={{ color: C.text, fontSize: 23, lineHeight: 34, fontWeight: "800", marginTop: 14 }}>
            {scenario.text}
          </Text>

          <Text style={{ color: C.muted, fontSize: 15, fontWeight: "800", marginTop: 22 }}>{t.choose}</Text>

          <View style={{ gap: 10, marginTop: 10 }}>
            {scenario.choices.map((item) => {
              const active = choice === item;
              return (
                <Pressable
                  key={item}
                  onPress={() => {
                    setChoice(item);
                    setRevealed(false);
                  }}
                  style={({ pressed }) => ({
                    minHeight: 62,
                    padding: 15,
                    borderRadius: 14,
                    borderWidth: active ? 2 : 1,
                    borderColor: active ? C.accent : C.border,
                    backgroundColor: active || pressed ? C.accentSoft : C.surface,
                    justifyContent: "center",
                  })}
                >
                  <Text style={{ color: C.text, fontSize: 16, lineHeight: 23, fontWeight: "800" }}>{item}</Text>
                </Pressable>
              );
            })}
          </View>

          <Pressable
            disabled={!choice}
            onPress={() => {
              setRevealed(true);
              playNarration(NARRATION_BY_SCENARIO_ID[scenario.id]);
            }}
            style={({ pressed }) => ({
              marginTop: 16,
              alignSelf: "flex-start",
              minHeight: 52,
              paddingHorizontal: 18,
              paddingVertical: 12,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: C.accent,
              backgroundColor: pressed ? C.surface : C.accentSoft,
              opacity: choice ? 1 : 0.4,
            })}
          >
            <Text style={{ color: C.text, fontSize: 16, fontWeight: "900" }}>{t.reveal}</Text>
          </Pressable>
        </View>

        <View style={{ flex: 1, minWidth: 390, padding: 22, borderRadius: 19, borderWidth: 1, borderColor: revealed ? (correct ? C.success : C.warning) : C.border, backgroundColor: C.surfaceRaised }}>
          {!revealed ? (
            <View style={{ minHeight: 320, alignItems: "center", justifyContent: "center" }}>
              <Text style={{ color: C.muted, fontSize: 18, lineHeight: 27, textAlign: "center" }}>
                {language === "es"
                  ? "Elige una explicación y después revela qué ocurrió."
                  : "Choose an explanation, then reveal what happened."}
              </Text>
            </View>
          ) : (
            <>
              <Text style={{ color: correct ? C.success : C.warning, fontSize: 15, fontWeight: "900" }}>
                {correct
                  ? language === "es" ? "BUENA ELECCIÓN" : "GOOD CHOICE"
                  : language === "es" ? "MIRA LA EXPLICACIÓN" : "LOOK AT THE EXPLANATION"}
              </Text>
              <Text style={{ color: C.text, fontSize: 25, lineHeight: 32, fontWeight: "900", marginTop: 8 }}>
                {scenario.answer}
              </Text>
              <Text style={{ color: C.text, fontSize: 16, lineHeight: 26, marginTop: 13 }}>{scenario.explanation}</Text>

              <Pressable
                onPress={next}
                style={({ pressed }) => ({
                  marginTop: 20,
                  alignSelf: "flex-start",
                  minHeight: 52,
                  paddingHorizontal: 18,
                  paddingVertical: 12,
                  borderRadius: 14,
                  borderWidth: 1,
                  borderColor: C.border,
                  backgroundColor: pressed ? C.accentSoft : C.surface,
                })}
              >
                <Text style={{ color: C.text, fontSize: 16, fontWeight: "900" }}>{t.another} →</Text>
              </Pressable>
            </>
          )}
        </View>
      </View>

      <View style={{ marginTop: 18 }}>
        <Text style={{ color: C.text, fontSize: 19, fontWeight: "900" }}>
          {language === "es" ? "Algunas fuentes comunes de error" : "Some common sources of error"}
        </Text>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 9, marginTop: 12 }}>
          {t.causes.map((cause) => (
            <View key={cause} style={{ paddingHorizontal: 13, paddingVertical: 10, borderRadius: 999, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}>
              <Text style={{ color: C.muted, fontSize: 14, fontWeight: "800" }}>{cause}</Text>
            </View>
          ))}
        </View>
      </View>

      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("ai_mistakes_confidence_not_correctness")}
        style={{ marginTop: 18, padding: 18, borderRadius: 16, borderLeftWidth: 5, borderWidth: 1, borderColor: C.border, borderLeftColor: C.warning, backgroundColor: C.accentSoft }}
      >
        <Text style={{ color: C.text, fontSize: 18, lineHeight: 27, fontWeight: "800" }}>{t.confidence}</Text>
      </AIExhibitNarrationPressable>
    </View>
  );
}
